import FileDownloaderButton from 'components/FileDownloaderButton';
import IconButton from 'components/IconButton';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  showFailureToast,
  showSuccessToast,
  showWarningToast,
} from 'lib/ToastHelper';
import React, { useCallback, useEffect } from 'react';
import { Col, Badge } from 'reactstrap';
import { appliedRowIdsAtom } from 'views/Admin/Dashboard/SASTDashboard2/state';

import {
  checkedRepoVulnAndBugsMapAtom,
  forceRerenderUIAtom,
  repositoryDataQueryParamsAtom,
  repositoriesDataAtom,
  selectedRowIdsAtom,
  totalVulnAndBugsForCheckedReposAtom,
} from './state';

const DashboardActions = () => {
  const listingData = useAtomValue(repositoriesDataAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);
  const [appliedSASTIds, setAppliedSASTIds] = useAtom(appliedRowIdsAtom);
  const [vulnAndBugsMap, setVulnAndBugsMap] = useAtom(
    checkedRepoVulnAndBugsMapAtom,
  );
  const forceRerenderUI = useSetAtom(forceRerenderUIAtom);
  const totalVulnAndBugs = useAtomValue(totalVulnAndBugsForCheckedReposAtom);
  const repositoryDataQueryParams = useAtomValue(repositoryDataQueryParamsAtom);

  const selectionChanged = (): boolean => {
    return appliedSASTIds.sort().join(',') !== selectedRowIds.sort().join(',');
  };

  const markAppliedRows = useCallback(
    (rowIds) => {
      if (listingData) {
        // TODO: update the applied row background color to indicate the applied rows
        for (let i = 0; i < listingData.results?.length; i++) {
          const row = listingData.results[i];
          row['isAppliedForDashboard'] = false;
        }
        const effectedRows = listingData?.results?.filter((row) =>
          rowIds.includes(row.id),
        );
        if (effectedRows?.length > 0) {
          effectedRows.forEach((row) => {
            row['isAppliedForDashboard'] = true;
          });
        }
        forceRerenderUI(Math.random().toString());
      }
    },
    [forceRerenderUI, listingData],
  );

  useEffect(() => {
    markAppliedRows(appliedSASTIds);
  }, [appliedSASTIds, listingData, markAppliedRows]);

  useEffect(() => {
    if (listingData?.results) {
      const newVulnAndBugsMap = { ...vulnAndBugsMap };

      // Remove counts for unchecked repositories
      Object.keys(newVulnAndBugsMap).forEach((id) => {
        if (!selectedRowIds.includes(Number(id))) {
          delete newVulnAndBugsMap[id];
        }
      });

      // Add combined vulnerability and bug counts for newly checked repositories
      listingData.results.forEach((repo) => {
        if (selectedRowIds.includes(repo.id)) {
          // Store total of vulnerabilities and bugs for each repository
          newVulnAndBugsMap[repo.id] =
            (repo.scan?.synced_vuln_count || 0) +
            (repo.scan?.synced_bugs_count || 0);
        }
      });

      setVulnAndBugsMap(newVulnAndBugsMap);
    }
  }, [selectedRowIds, listingData, setVulnAndBugsMap]);

  const onUpdateDashboardClick = () => {
    // check if the total vulnerability and bug count for any of the selected repositories is greater than zero
    if (totalVulnAndBugs > 0 || selectedRowIds?.length === 0) {
      markAppliedRows(selectedRowIds);
      setAppliedSASTIds(selectedRowIds);
    } else {
      showWarningToast(
        'Dashboard can not be applied, as for the selected repositories, the vulnerabilities/bugs total count is zero. Please try a different combination of repositories.',
      );
    }
  };

  // Keep CSV export aligned with the repository grid by reusing the same
  // query params; selected rows go in the body to avoid very long query strings.
  const getExportUrlParams = () => ({
    ...(repositoryDataQueryParams || {}),
  });

  const getExportRequestData = () =>
    selectedRowIds?.length > 0 ? { host_id_list: selectedRowIds } : null;

  return (
    <Col
      className="text-right"
      style={{ display: 'flex', alignItems: 'center' }}
    >
      {selectedRowIds?.length > 0 && (
        <Col md="3" sm="12" className="text-left text-nowrap">
          {selectedRowIds?.length > 0 && (
            <>
              ( Selected:{' '}
              <label
                className="label btn"
                style={{
                  backgroundColor: 'rgba(7, 114, 219, 0.1)',
                  color: 'black',
                }}
              >
                {selectedRowIds.length}
              </label>
              )
            </>
          )}
        </Col>
      )}
      <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
        {selectedRowIds?.length > 0 && (
          <>
            <IconButton
              id="clearSelectionButton"
              iconClass="fas fa-times"
              tooltip="Clear all selections"
              onClick={() => setSelectedRowIds([])}
            />
            <div
              style={{
                width: '1px',
                height: '24px',
                backgroundColor: '#dee2e6',
                margin: '0 5px',
              }}
            />
          </>
        )}
        <IconButton
          id="updateButton"
          iconClass="fa fa-chart-line fa-lg"
          tooltip="Update Dashboard For Selected Repositories"
          disabled={
            !selectionChanged() ||
            (selectedRowIds.length === 0 && appliedSASTIds.length === 0)
          }
          active={appliedSASTIds.length > 0}
          stale={selectionChanged()}
          onClick={onUpdateDashboardClick}
        />
        <div style={{ position: 'relative' }}>
          <IconButton
            id="resetButton"
            iconClass="fa fa-undo fa-lg"
            tooltip={
              appliedSASTIds?.length > 0 ? (
                <>
                  Reset dashboard to show all repositories
                  <br />
                  Applied ( {appliedSASTIds.length} repositories )
                </>
              ) : (
                'Reset dashboard to show all repositories'
              )
            }
            disabled={appliedSASTIds.length === 0}
            onClick={() => {
              markAppliedRows([]);
              setAppliedSASTIds([]);
            }}
          />
          {appliedSASTIds?.length > 0 && (
            <Badge
              color="transparent"
              pill
              style={{
                position: 'absolute',
                top: '-7px',
                right: '-6px',
                fontSize: '0.70rem',
                color: '#fff',
                textShadow:
                  '-1px -1px 0 black, 1px -1px 0 black, -1px 1px 0 black, 1px 1px 0 black',
                cursor: 'pointer',
              }}
            >
              {appliedSASTIds.length}
            </Badge>
          )}
        </div>
        <FileDownloaderButton
          buttonId="sast_export_all"
          iconClass="fa fa-file-export"
          tooltip="Export CSV data for filtered Repositories"
          fileName={`${localStorage
            .getItem('currentCompany')
            ?.toLocaleLowerCase()}_repositories.csv`}
          method={selectedRowIds?.length > 0 ? 'POST' : 'GET'}
          downloadUrl="sast/repositories/export-csv/"
          urlParams={getExportUrlParams()}
          data={getExportRequestData()}
          onSuccessCallback={() => showSuccessToast('File download completed.')}
          onFailureCallback={(message) => showFailureToast(message)}
        />
      </div>
    </Col>
  );
};

export default DashboardActions;
